import { useCallback, useEffect, useState } from 'react';
import {
  Alert,
  Box,
} from '@mui/material';
import { useTranslation } from 'react-i18next';

import {
  DetailDrawer,
  HeaderBar,
  RankingsTable,
  SummaryTiles,
  TopUpsideSection,
} from '../../components/ichimoku-transition';
import { filterScanRows } from '../../components/ichimoku-transition/shared';
import { ichimokuTransitionAPI } from '../../services/api';

export default function IchimokuTransition() {
  const { t, i18n } = useTranslation();
  const [loading, setLoading] = useState(true);
  const [calculating, setCalculating] = useState(false);
  const [error, setError] = useState(null);
  const [source, setSource] = useState('persisted');
  const [scoreDate, setScoreDate] = useState('');
  const [minScore, setMinScore] = useState(55);
  const [stateFilter, setStateFilter] = useState('ALL');
  const [confidence, setConfidence] = useState('ALL');
  const [signalType, setSignalType] = useState('ALL');
  const [search, setSearch] = useState('');
  const [lookupTicker, setLookupTicker] = useState('');
  const [results, setResults] = useState([]);
  const [topUpsideRows, setTopUpsideRows] = useState([]);
  const [meta, setMeta] = useState({ scoreDate: null, total: 0 });
  const [drawerTicker, setDrawerTicker] = useState(null);
  const [detail, setDetail] = useState(null);
  const [historyLoading, setHistoryLoading] = useState(false);

  const fetchScan = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const params = {
        min_score: minScore,
        limit: 200,
      };
      if (scoreDate) params.score_date = scoreDate;

      const upsideParams = {
        min_score: minScore,
        limit: 10,
        ...(scoreDate ? { score_date: scoreDate } : {}),
      };

      const [response, topUpsideResponse] = await Promise.all([
        source === 'live'
          ? ichimokuTransitionAPI.getLiveScan(params)
          : ichimokuTransitionAPI.getScan(params),
        source === 'live'
          ? ichimokuTransitionAPI.getLiveTopUpside(upsideParams)
          : ichimokuTransitionAPI.getTopUpside(upsideParams),
      ]);

      const payload = response.data || {};
      const rows = payload.results || [];
      setResults(rows);
      setTopUpsideRows(topUpsideResponse.data?.results || []);
      setMeta({
        scoreDate: payload.score_date || null,
        total: payload.pagination?.total || rows.length,
      });
    } catch (requestError) {
      setError(requestError.response?.data?.detail || requestError.message);
      setResults([]);
      setTopUpsideRows([]);
      setMeta({ scoreDate: null, total: 0 });
    } finally {
      setLoading(false);
    }
  }, [minScore, scoreDate, source]);

  useEffect(() => {
    fetchScan();
  }, [fetchScan]);

  const openTicker = useCallback(async (ticker) => {
    if (!ticker) return;
    const normalized = ticker.toUpperCase();
    setDrawerTicker(normalized);
    setDetail(null);
    setHistoryLoading(true);
    try {
      const response = await ichimokuTransitionAPI.getTicker(normalized, 60);
      setDetail(response.data);
    } catch (requestError) {
      setDetail({ error: requestError.response?.data?.detail || requestError.message });
    } finally {
      setHistoryLoading(false);
    }
  }, []);

  const handleCalculate = useCallback(async () => {
    try {
      setCalculating(true);
      setError(null);
      await ichimokuTransitionAPI.calculate(scoreDate || undefined);
      if (source !== 'persisted') {
        setSource('persisted');
      }
      await fetchScan();
    } catch (requestError) {
      setError(requestError.response?.data?.detail || requestError.message);
    } finally {
      setCalculating(false);
    }
  }, [fetchScan, scoreDate, source]);

  const filteredRows = filterScanRows(results, {
    search,
    stateFilter,
    confidence,
    signalType,
  });
  const stateCounts = filteredRows.reduce((acc, row) => {
    const key = row.state || 'UNKNOWN';
    acc[key] = (acc[key] || 0) + 1;
    return acc;
  }, {});
  const highConfidence = filteredRows.filter((row) => row.confidence === 'HIGH').length;
  const actionable = filteredRows.filter((row) => row.signal?.rr_ratio && row.signal.rr_ratio >= 1.5).length;
  const avgScore = filteredRows.length
    ? (filteredRows.reduce((sum, row) => sum + (Number(row.score) || 0), 0) / filteredRows.length).toFixed(1)
    : '0.0';

  return (
    <Box sx={{ px: 0, py: 2 }}>
      <HeaderBar
        metaScoreDate={meta.scoreDate}
        source={source}
        lookupTicker={lookupTicker}
        onLookupChange={(event) => setLookupTicker(event.target.value.toUpperCase().replace(/[^A-Z0-9]/g, '').slice(0, 8))}
        onLookupKeyDown={(event) => {
          if (event.key === 'Enter' && lookupTicker.trim()) openTicker(lookupTicker.trim());
        }}
        onOpenLookup={() => openTicker(lookupTicker.trim())}
        calculating={calculating}
        onCalculate={handleCalculate}
        onRefresh={fetchScan}
        t={t}
      />

      {error ? <Alert severity="error" sx={{ mb: 2, borderRadius: 1.5 }}>{error}</Alert> : null}

      <Box sx={{ mb: 3 }}>
        <SummaryTiles
          filteredCount={filteredRows.length}
          totalCount={meta.total}
          highConfidence={highConfidence}
          actionable={actionable}
          avgScore={avgScore}
          t={t}
        />
      </Box>

      {!loading ? (
        <TopUpsideSection
          rows={topUpsideRows}
          onOpenTicker={openTicker}
          t={t}
          language={i18n.resolvedLanguage || i18n.language}
        />
      ) : null}

      <Box>
        <RankingsTable
          loading={loading}
          filteredRows={filteredRows}
          onOpenTicker={openTicker}
          t={t}
          language={i18n.resolvedLanguage || i18n.language}
          source={source}
          onSourceChange={setSource}
          scoreDate={scoreDate}
          onScoreDateChange={setScoreDate}
          minScore={minScore}
          onMinScoreChange={setMinScore}
          stateCounts={stateCounts}
          stateFilter={stateFilter}
          onStateFilterChange={setStateFilter}
          confidence={confidence}
          onConfidenceChange={setConfidence}
          signalType={signalType}
          onSignalTypeChange={setSignalType}
          search={search}
          onSearchChange={setSearch}
        />
      </Box>

      <DetailDrawer
        open={Boolean(drawerTicker)}
        ticker={drawerTicker}
        historyLoading={historyLoading}
        detail={detail}
        onClose={() => setDrawerTicker(null)}
        t={t}
        language={i18n.resolvedLanguage || i18n.language}
      />
    </Box>
  );
}